import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { AnnouncementModel } from '@app/core/model/announcement.model';
import { UserListService } from '@app/core/service/user-list.service';
import { EAnnouncementActions } from '@backend/models/types/announcement';

@Component({
  selector: 'app-announcement-item',
  templateUrl: './announcement-item.component.html',
  styleUrls: ['./announcement-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AnnouncementItemComponent {
  @Input()
  public readonly announcement: AnnouncementModel;

  @Input()
  public readonly animationOrder?: number = -1;

  @Input()
  public readonly showViewCount?: boolean = false;

  private readonly _activeUserIds = new Set<string>();

  @HostBinding('style')
  get myStyle(): SafeStyle {
    return this._sanitizer.bypassSecurityTrustStyle(
      `--animation-order: ${this.animationOrder};`
    );
  }

  @HostBinding('class.animated')
  public get isAnimated() {
    return this.animationOrder !== -1;
  }

  @HostBinding('class.unacknowledged')
  public get unacknowledgedClass() {
    return this.announcement.unacknowledged;
  }

  public constructor(
    private readonly _sanitizer: DomSanitizer,
    private readonly _userListService: UserListService
  ) {
    this._userListService.users$.subscribe((users) => {
      this._activeUserIds.clear();
      users
        .filter((u) => !u.isDeleted)
        .forEach((u) => this._activeUserIds.add(String(u._id)));
    });
  }

  protected get acknowledged() {
    if (this._activeUserIds.size === 0) {
      return this.announcement.recipients.filter((a) => !!a.acknowledged);
    }
    return this.announcement.recipients.filter(
      (a) => !!a.acknowledged && this._activeUserIds.has(String(a.id))
    );
  }

  protected get acknowledgedCount() {
    if (this.announcement.actions === EAnnouncementActions.ONLY_RESPOND) {
      if (this._activeUserIds.size === 0) {
        return this.announcement.recipients.filter((a) => !!a.read).length;
      }
      return this.announcement.recipients.filter(
        (a) => !!a.read && this._activeUserIds.has(String(a.id))
      ).length;
    }
    return this.acknowledged.length;
  }

  protected get totalActiveRecipientsCount() {
    if (this._activeUserIds.size === 0) {
      return this.announcement.recipients.length;
    }
    return this.announcement.recipients.filter((a) =>
      this._activeUserIds.has(String(a.id))
    ).length;
  }
}
